import React from "react";
import Layout from "components/Layout";
import TermBlocks from "components/TermBlocks";
import Padder from "components/Padder";
import Footer from "components/Footer";

const PAGE_CONFIG = [
  {
    header: "Introduction 👋",
    blocks: [
      {
        header: "",
        blocks: [
          `Hi there! Thanks for taking the time to understand the DevPlan, Inc.
          (“PriceBlocs,” “we,” “our,” or “us”, "the platform") Privacy Policy.
          Outlined below is the Privacy Policy by which you consent to our use
          of your data by using our Services. Our registered users (“Members”)
          build professional development plans, manage those plans themselves
          and with their teammates, and view content on priceblocs.com related
          to professional development. Content on some of our services is also
          available to non-members (“Visitors”).`,
          `By using our Services, you consent to our use of your data under
          this Privacy Policy.`
        ]
      },
      {
        header: "Data Controllers",
        blocks: [
          `You are entering into the User Agreement with DevPlan, Inc., who will
          be responsible for your personal data provided to, or collected by
          or for, our Services.`
        ]
      },
      {
        header: "Services",
        blocks: [
          `This Privacy Policy applies to priceblocs.com, PriceBlocs-related
          sites, apps, communications and services (“Services”). However the
          Privacy Policy does not apply to services that they are offered
          under a different privacy policy, for example, the Google reCAPTCHA
          Service which we use on priceblocs.com to protect from spam and
          abuse traffic.`
        ]
      },
      {
        header: "Consent",
        blocks: [
          `If you use our Services, you consent to the collection, use and sharing of your personal data under this Privacy Policy and agree to the User Agreement.`
        ]
      },
      {
        header: "Policy changes and notifications",
        blocks: [
          `This Privacy Policy may be modified over time. Any material changes to the Policy made by us will be proceeded with a notice through our Services or other means, to communicate the intended changes so that Members and Visitors have an opportunity to review them before they become effective.`,
          `Our Services are actively built upon and we create new features regularly. If such new features would result in a material change to the collection of your personal data or how we use it, we will notify you as previously described and modify this Privacy Policy to reflect said material changes.`,
          `Your continued use of our Services after the effective date of the amendments to the Privacy Policy means that you are consenting to the Privacy Policy.`,
          `If you ever want to chat about past, current or future Privacy Policy matters, reach out to us at the contact information listed at the end of this page.`
        ]
      }
    ]
  },
  {
    header: "Information we collect",
    blocks: [
      {
        header: "Account creation",
        blocks: [
          `To create an account, Members will provide at minimum an email address. We provide alternate optional methods of account creation through 3rd party services, each of which will present a consent screen at the time of account creation so that you can review what personal information will be made available to facilitate account creation.`
        ]
      },
      {
        header: "Webpages and pricing plans",
        blocks: [
          `Members build their webpages by choosing the components and adding descriptions around their products and prices. Some pages like the pricing page are integrated with Stripe, so members can associate their Stripe products and prices with content on the page like calls to action.`,
          `Pages which members choose to publish will be made public on the internet. Members can choose to sell their products on PriceBlocs, and payments will be processed through their connected Merchant Account. An application fee percent may be applied to the subtotal of goods sold through PriceBlocs pages, if the Members account is subscribed to a PriceBlocs product tier which includes such a fee on charges made through their pages built on PriceBlocs. Supported Merchant Accounts include Stripe.`
        ]
      },
      {
        header: "Data retention",
        blocks: [
          `We will process and store your information for as long as needed to fulfill the function of storage and or as permitted by law. We will retain Member account information whilst the Members account is open.`,
          `When a Member closes their account, we will store some or all of their information up to 5 years after closure of the account. If the information is not deemed required to fulfill or inform the functionality of our Services it will be deleted. However if such information is required or necessary for record-keeping, contracting or legal compliance it will not be deleted.`,
          `Information in a Member account about other individuals (such as Associates or other Members) is treated as Member information, to the extent permitted by law.`
        ]
      },
      {
        header: "Contact Information",
        blocks: [
          `We will receive personal data about you when others import contact information from third party services like Slack or Google GSuite etc. An example of this would be when a Member wants to send sign up invitations to their colleagues for the platform from our Services.`
        ]
      },
      {
        header: "Service Use",
        blocks: [
          `We log and collect usage data when you visit and use our Services in any way. Examples of this would be when you view or click on content, we collect such usage information to inform how we should build, manage and improve our Services.`,
          `Examples of such data we collect are log in timestamps, internet protocol ("IP") addresses, device information etc to identify you and log your usage of our Services.`
        ]
      },
      {
        header: "Cookies, Web Beacons and Other Similar Technologies",
        blocks: [
          `We use cookies and similar technologies (e.g., web beacons, pixels, ad tags and device identifiers) to recognize you and/or your device(s) on, off and across different Services and devices. Some cookies are functionally required to operate our Services. Cookies can be controlled via your browser settings. Services we use to perform key functions like Customer Support, use cookies to function.`,
          `We use services like Mixpanel, Google Analytics, Google Analytics Advertising features such as Remarketing with Google Analytics, Google Display Network Impression Reporting, Google Analytics Demographics and Interest Reporting, and additional integrated Google services that require Google Analytics to collect data for advertising purposes such as Google Ads, Ad Exchange, Campaign Manager, Search Console, and BigQuery, and other third-party services to improve the performance of the Services and for analytics, advertising, and marketing purposes.`,
          `These services gather website usage information and may be combined with other data we collect about Members or Visitors to our site.`,
          `Our cookie policy can be read in full at https://priceblocs.com/cookie-policy`
        ]
      },
      {
        header: "Your Device and Location",
        blocks: [
          `Our Services will receive information about your browser navigation such as the referring url when you visit our Services and the destination url when you leave. Our Services also receive IP information, operating system, device identifiers and features and service provider or mobile carrier.`,
          `We receive location data from mobile devices which are connected to our services unless you prevent location information being shared from your device.`
        ]
      },
      {
        header: "Information requests",
        blocks: [
          `You have the right to make such requests as to request the personal information we hold about you, request that we rectify or erase your personal information, request we restrict or block the processing of your personal information.`,
          `We may be unable to afford these rights to you under certain circumstances, such as if we are legally prevented from doing so. To make a request, contact us with this address privacy@priceblocs.com`
        ]
      }
    ]
  },
  {
    header: "How we use your information",
    blocks: [
      {
        header: "",
        blocks: [
          `We use the data we collect from our Members' usage and interaction with the platform to provide, support, personalize and make our Services more relevant and useful to you and others.`
        ]
      },
      {
        header: "Services",
        blocks: [
          `We use your data to authenticate you and authorize access to our Services.`
        ]
      },
      {
        header: "Communications",
        blocks: [
          `We will contact you through mobile, email, notices posted on our websites or apps, and other ways through our Services, including text messages and push notifications. You will receive communications related to our Services and your use of them.`,
          `We will contact you through various means via our Services, which can include email, push notifications and notices posted on our sites and apps. We will also send messages about our Services availability , security, or other service-related issues. You may not opt for communications which relate to security and legal notices from us whilst your account is open. If you choose to delete your account, you will no longer receive communications from us.`
        ]
      },
      {
        header: "Marketing",
        blocks: [
          `We send our Members marketing communications to promote new and existing Services we provide.`
        ]
      },
      {
        header: "Service Development",
        blocks: [
          `We use data we acquire from Members, Visitors and others to inform and guide our Services research and development, in an effort to continually improve our Services and help to generate more Services value for our Members.`
        ]
      },
      {
        header: "Surveys and polls",
        blocks: [
          `We use surveys and polls to gather Member and Visitor perspectives, insights, feedback and opinions about our Services and the industries within which we operate. Surveys and polls may also include questions which the responder could optionally respond to with personal information. Members and visitors are not obligated to respond to such surveys and polls.`
        ]
      },
      {
        header: "Customer support",
        blocks: [
          `We provide Customer Support over email and optionally through other 3rd party services like Intercom or similar. Data we acquire from these communications can be used to investigate, respond and resolve Service issues and complaints.`
        ]
      },
      {
        header: "Aggregate insights",
        blocks: [
          `We generate aggregate insights and statistics about our Members and Visitors. These aggregations can be related to the usage of our Services and also combined with external data sources to produce different aggregations and statistics.`
        ]
      },
      {
        header: "Security and Investigations",
        blocks: [
          `We will investigate possible instances of fraud and violations against our User Agreement, Terms of Service and Privacy Policy. We will use your data and communications if necessary to perform such investigations. We will use your data and communications if necessary to perform such investigations.`
        ]
      }
    ]
  },
  {
    header: "How we share your information",
    blocks: [
      {
        header: "Legal Disclosures",
        blocks: [
          `It is possible that we will need to disclose information about you when required by law, subpoena, or other legal process or if we have a good faith belief that disclosure is reasonably necessary to (1) investigate, prevent, or take action regarding suspected or actual illegal activities or to assist government enforcement agencies; (2) enforce our agreements with you, (3) investigate and defend ourselves against any third-party claims or allegations, (4) protect the security or integrity of our Service (such as by sharing with companies facing similar threats); or (5) exercise or protect the rights and safety of PriceBlocs, our Members, personnel, or others. We attempt to notify Members about legal demands for their personal data when appropriate in our judgment, unless prohibited by law or court order or when the request is an emergency. We may dispute such demands when we believe, in our discretion, that the requests are overbroad, vague or lack proper authority, but we do not promise to challenge every demand.`
        ]
      },
      {
        header: "Change in control or Sale",
        blocks: [
          `We can share your personal data in the event of an actual or preparation for a potential sale, merger, change of control, or other transfer of all or part of our assets including as part of a bankruptcy proceeding.`,
          `Any other entity which buys us or part of our business will have the right to continue to use your data, but only in the manner set out in this Privacy Policy unless you agree otherwise.`
        ]
      }
    ]
  },
  {
    header: "Other important information",
    blocks: [
      {
        header: "Security",
        blocks: [
          `We implement security safeguards designed to protect your data, such as HTTPS for ensuring encrypted data in transit. We used 3rd party platforms such as Heroku which encrypt data at rest. We monitor fraudulent traffic to our Services and monitor our systems for possible vulnerabilities and attacks. However, we cannot warrant the security of any information that you send us. There is no guarantee that data may not be accessed, disclosed, altered, or destroyed by breach of any of our physical, technical, or managerial safeguards.`
        ]
      },
      {
        header: "Third party service privacy policies",
        blocks: [
          `PriceBlocs uses the Google Maps API(s) which has it's own privacy policy here https://www.google.com/policies/privacy. By using PriceBlocs you agree to the PriceBlocs privacy policy and to be bound by the PriceBlocs terms of service (https://priceblocs.com/terms) and also by Google’s Terms of Service https://www.google.com/intl/en/policies/terms.`
        ]
      },
      {
        header: "How to contact us",
        blocks: [
          `If you want to chat about something with us, use this contact address privacy@priceblocs.com. You can also find the means to contact us at the bottom of the website page https://priceblocs.com.`
        ]
      }
    ]
  }
];

const Privacy = () => (
  <Layout>
    {() => (
      <div className="mw8 center pt5">
        <div className="ph3">
          <article>
            <div className="f1 fw5">Privacy Policy</div>
            <div className="f6 pt2 lh-copy">Last updated Feb 28th, 2022</div>
          </article>
          <TermBlocks config={PAGE_CONFIG} />
          <Padder customClasses="dib h4 w-100" />
        </div>
        {/* <!-- Footer  --> */}
        <div className="hairline-1 bt">
          <Footer />
        </div>
      </div>
    )}
  </Layout>
);

export default Privacy;
